<template>
  <div class="flex flex-col h-full gap-3">
    <div v-if="field.type === CustomFieldTypeEnum.Select" class="flex flex-wrap gap-2">
      <div
        v-for="option in field.options"
        :key="option"
        class="px-4 py-2 rounded-full border cursor-pointer text-sm select-none"
        :class="value === option ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium' : 'border-gray-300 text-gray-700'"
        @click="value = option"
      >
        {{ option }}
      </div>
    </div>
    <div v-else class="relative">
      <InputText
        v-model="value"
        class="w-full"
        :inputmode="field.type === CustomFieldTypeEnum.Number ? 'decimal' : 'text'"
        :placeholder="field.type === CustomFieldTypeEnum.Number ? '0' : 'הקלידו ערך'"
      />
      <span v-if="field.unit" class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">{{ field.unit }}</span>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm text-gray-500">תאריך</label>
      <ResponsiveDatePicker
        :modelValue="date"
        :maxDate="today"
        title="תאריך הערך"
        @update:modelValue="onDatePicked"
      />
      <span class="text-xs text-gray-400">ברירת המחדל היא היום — אפשר לתעד ערך שנמדד בתאריך קודם.</span>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm text-gray-500">הערה (רשות)</label>
      <Textarea
        v-model="note"
        class="w-full"
        rows="2"
        :autoResize="false"
        :maxlength="CUSTOM_FIELD_NOTE_MAX_LENGTH"
        placeholder="למשל: הועלה בעקבות תגובה טובה בטיפול הקודם"
      />
    </div>

    <span v-if="entry" class="text-xs text-gray-400">עריכת ערך קיים — לא ייווצר רישום חדש בהיסטוריה.</span>
    <span v-else-if="field.trackHistory" class="text-xs text-gray-400">הערך הקודם יישמר בהיסטוריית השינויים.</span>

    <drawer-footer
      class="mt-auto"
      approve-label="שמירה"
      :approveDisable="!value.trim() || loading"
      :approveLoading="loading"
      @approve-click="save"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
  CUSTOM_FIELD_NOTE_MAX_LENGTH,
  CustomFieldTypeEnum,
  ICustomFieldDefinition,
  ICustomFieldHistoryEntry
} from '@torim-app/types';
import ResponsiveDatePicker from '@app/components/admin/components/ResponsiveDatePicker.vue';

const props = defineProps<{
  field: ICustomFieldDefinition;
  initialValue?: string;
  // When present the drawer edits this existing entry in place instead of recording a new one.
  entry?: ICustomFieldHistoryEntry;
  onSave: (payload: { value: string; date: Date; note?: string }) => Promise<void>;
}>();

const emit = defineEmits(['close']);

const today = new Date();

const value = ref(props.entry?.value ?? props.initialValue ?? '');
const date = ref<Date>(props.entry ? new Date(String(props.entry.date)) : today);
const note = ref(props.entry?.note ?? '');
const loading = ref(false);

const onDatePicked = (picked?: Date | Date[]) => {
  const nextDate = Array.isArray(picked) ? picked[0] : picked;
  if (nextDate) date.value = nextDate;
};

const save = async () => {
  if (!value.value.trim()) return;

  loading.value = true;
  try {
    await props.onSave({
      value: value.value.trim(),
      date: date.value,
      note: note.value.trim() || undefined
    });
    emit('close');
  } finally {
    loading.value = false;
  }
};
</script>
